import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { endPoints } from "../../utils/endPoints";
import checkAuth from "../../app/auth";

/* =========================
 ✅ SUPER ADMIN DASHBOARD
========================= */
export const fetchSuperAdminDashboard = createAsyncThunk(
  "dashboard/superAdmin",
  async (_, { rejectWithValue }) => {
    try {
      const res = await checkAuth.get(
        `${process.env.REACT_APP_BASE_URL}${endPoints.SUPER_ADMIN_DASHBOARD}`,
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Dashboard fetch failed",
      );
    }
  },
);

/* =========================
 ✅ SCHOOL ADMIN DASHBOARD
========================= */
export const fetchSchoolAdminDashboard = createAsyncThunk(
  "dashboard/schoolAdmin",
  async (_, { rejectWithValue }) => {
    try {
      const res = await checkAuth.get(
        `${process.env.REACT_APP_BASE_URL}${endPoints.SCHOOL_ADMIN_DASHBOARD}`,
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Dashboard fetch failed",
      );
    }
  },
);

/* =========================
 ✅ CREATE SCHOOL
========================= */
export const createSchool = createAsyncThunk(
  "school/create",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.CREATE_SCHOOL}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

/* =========================
 ✅ GET SCHOOLS
========================= */
export const getSchools = createAsyncThunk(
  "school/getAll",
  async (
    { page = 1, page_size = 10, search = "", status = "" },
    { rejectWithValue },
  ) => {
    try {
      const res = await checkAuth.get(
        `${process.env.REACT_APP_BASE_URL}${endPoints.GET_SCHOOLS}`,
        {
          params: {
            page,
            page_size,
            search,
            status,
          },
        },
      );

      return res.data; // 👈 returns your exact API payload
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Fetch failed");
    }
  },
);

/* =========================
 ✅ UPDATE SCHOOL
========================= */
export const updateSchool = createAsyncThunk(
  "school/update",
  async ({ school_id, formData }, { rejectWithValue }) => {
    try {
      const res = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.UPDATE_SCHOOL}?school_id=${school_id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Update failed");
    }
  },
);

/* =========================
 ✅ UPDATE SCHOOL STATUS
========================= */
export const updateSchoolStatus = createAsyncThunk(
  "school/update",
  async ({ school_id, status }, { rejectWithValue }) => {
    try {
      const res = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.STATUS_UPDATE_SCHOOL}?school_id=${school_id}&status=${status}`,
      );

      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Update failed");
    }
  },
);

/* =========================
 ✅ DELETE SCHOOL
========================= */
export const deleteSchool = createAsyncThunk(
  "school/delete",
  async (id, { rejectWithValue }) => {
    try {
      const res = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.DELETE_SCHOOL}?school_id=${id}`,
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Delete failed");
    }
  },
);

// RESET LINK PASSWORD
// RESET LINK PASSWORD
export const resetLink = createAsyncThunk(
  "school/resetLink",
  async ({ email, role, baseURLLink }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.SUPER_ADMIN_RESET_LINK}`,
        {
          email,
          role,
          base_url: `${baseURLLink}/reset-password`,
        },
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  },
);

// RESET PASSWORD
let localToken = localStorage.getItem("forgetToken");
export const resetPassword = createAsyncThunk(
  "school/resetPassword",
  async ({ password, token }, { rejectWithValue }) => {
    try {
      let res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.SUPER_ADMIN_NEW_PASSWORD}`,

        { token, password },
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "reset failed");
    }
  },
);

export const getCurriculum = createAsyncThunk(
  "school/getSingle",
  async (_, { rejectWithValue }) => {
    try {
      const res = await checkAuth.get(
        `${process.env.REACT_APP_BASE_URL}${endPoints.GET_ALL_CURRICULUM}`,
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Fetch failed");
    }
  },
);

export const schoolChangePassword = createAsyncThunk(
  "school/changePassword",
  async (
    { school_id, new_password, confirm_password },
    { rejectWithValue },
  ) => {
    try {
      const res = await checkAuth.post(
        `${process.env.REACT_APP_BASE_URL}${endPoints.SUPER_ADMIN_CHANGE_SCHOOL_ADMIN_PASSWORD}`,
        {
          school_id,
          new_password,
          confirm_password,
        },
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  },
);

/* =========================
 ✅ SLICE
========================= */
const createSchoolSlice = createSlice({
  name: "school",
  initialState: {
    loading: false, // ✅ ONE loader for ALL requests
    success: false, // ✅ ONE success for ALL requests
    error: null,

    schools: [], // ✅ all schools stored here
    dashboardStats: null,
    schoolAdminStats: null,
  },

  reducers: {
    resetSchoolState: (state) => {
      state.loading = false;
      state.success = false;
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder

      /* ✅ SCHOOL ADMIN DASHBOARD */
      .addCase(fetchSchoolAdminDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSchoolAdminDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.schoolAdminStats = action.payload.statistics;
      })
      .addCase(fetchSchoolAdminDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ✅ SUPER ADMIN DASHBOARD */
      .addCase(fetchSuperAdminDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSuperAdminDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboardStats = action.payload.statistics;
      })
      .addCase(fetchSuperAdminDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ✅ CREATE */
      .addCase(createSchool.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createSchool.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.schools.push(action.payload);
      })
      .addCase(createSchool.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      });
  },
});

// export const { resetSchoolState } = createSchoolSlice.actions;
export default createSchoolSlice.reducer;
